import * as React from "react";
import Header from "./UI/Header";
import {
  FluentProvider,
  makeStyles,
  BrandVariants,
  createDarkTheme,
  createLightTheme,
  Theme
} from "@fluentui/react-components";
import { I18nextProvider } from 'react-i18next';
import i18n from "../common/locales/i18nConfig";
import { MemoryRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import ReactGA from 'react-ga';
import HomePage from "./pages/HomePage";
import SettingsPage from "./pages/SettingsPage";
import { SettingsProvider, useSettings } from "../contexts/SettingsContext";
import InfoPage from "./pages/InfoPage";

const TRACKING_ID = "G-DQ219X19TX";
ReactGA.initialize(TRACKING_ID);

// Generated by: https://storybooks.fluentui.dev/react/?path=/docs/theme-theme-designer--docs
// Excel green brand colors
const excelTheme: BrandVariants = {
    10: "#020401",
    20: "#101C0E",
    30: "#162F16",
    40: "#1A3C1A",
    50: "#1D4B1F",
    60: "#1F5923",
    70: "#216827",
    80: "#23782B",
    90: "#23882F",
    100: "#239833",
    110: "#23A837",
    120: "#21B93C",
    130: "#1ECA40",
    140: "#19DC44",
    150: "#11ED48",
    160: "#00FF4C"
};

// Create light and dark themes from the Excel green brand
const lightTheme: Theme = {
    ...createLightTheme(excelTheme),
};

const darkTheme: Theme = {
    ...createDarkTheme(excelTheme),
};

// Adjust dark theme brand colors for better visibility
darkTheme.colorBrandForeground1 = excelTheme[110];
darkTheme.colorBrandForeground2 = excelTheme[120];

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
    minWidth: "300px",
  },
});

// Determine the page to land on for the very first render.
// New users (no saved setting, or no hasSeenQuickStart flag) start on the
// Quick Start / Info page; returning users start on Home.
const getInitialRoute = (): string => {
  try {
    const saved = localStorage.getItem('excel-ai-settings');
    if (!saved) return '/info';
    const parsed = JSON.parse(saved);
    return parsed.hasSeenQuickStart ? '/' : '/info';
  } catch {
    return '/info';
  }
};

// Create a wrapper component that uses the settings context
const AppContent: React.FC = () => {
  const styles = useStyles();
  const { toggleTheme, isDarkMode, settings, updateSettings } = useSettings();

  const initialRoute = React.useMemo(() => getInitialRoute(), []);

  React.useEffect(() => {
    ReactGA.pageview(globalThis.location.pathname + globalThis.location.search);
  }, []);

  // Mark the Quick Start Guide as seen on first launch so it is only shown once.
  React.useEffect(() => {
    if (!settings.hasSeenQuickStart) {
      updateSettings({ hasSeenQuickStart: true });
    }
  }, [settings.hasSeenQuickStart, updateSettings]);

  return (
    <FluentProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <Router initialEntries={[initialRoute]}>
        <div className={styles.root}>
          <Header logo="assets/logo-filled.png" />
          
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/info" element={<InfoPage />}/>
            <Route path="/settings" element={
              <SettingsPage 
                isDarkMode={isDarkMode} 
                onToggleTheme={toggleTheme} 
              />
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </Router>
    </FluentProvider>
  );
};

const App: React.FC = () => {
  return (
    <I18nextProvider i18n={i18n}>
      <SettingsProvider>
        <AppContent />
      </SettingsProvider>
    </I18nextProvider>
  );
};

export default App;